import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewEncapsulation,
} from "@angular/core";
import { Subject, Subscription, of } from "rxjs";
import { debounceTime, switchMap } from "rxjs/operators";

import { LocalizeService, AuthService } from "../../insa-services";

import {
  ControlTableService,
  TraderSecurityDtoModel,
} from "./control-table.service";
import { ControlTableSecurity } from "./control-table.models";

@Component({
  selector: "app-control-table-order-modal",
  templateUrl: "./control-table-order-modal.component.html",
  styles: [require("./control-table.component.css").toString()],
  encapsulation: ViewEncapsulation.None,
})
export class ControlTableOrderModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() security: ControlTableSecurity | null = null;
  @Input() side: "Buy" | "Sell" = "Buy";
  @Input() pickMode = false;
  @Input() currency = "";

  @Output() closed = new EventEmitter<void>();
  @Output() removeSecurity = new EventEmitter<number>();

  searchQuery = "";
  searchResults: TraderSecurityDtoModel[] = [];
  showSearchResults = false;

  isDomestic = true;
  allowedTransactionTypes: number[] = [];
  selectedTransactionType = 2;
  allowedOrderTypes: number[] = [];

  selectedValidity = "Day";
  goodTillDate = "";

  tradeAmount = 0;
  tradePrice = 0;
  tradeStopPrice = 0;
  tradeTotal = 0;

  currentPrice = 0;
  availableLiquidity = 0;
  heldQuantity = 0;
  secReservationPercent = 1;

  cashLoading = false;
  heldLoading = false;

  liveCheckAmounts: { [key: string]: number } | null = null;
  liveCheckLoading = false;

  isChecking = false;
  isSubmitting = false;
  orderError = "";
  orderResult: { success: boolean; description: string } | null = null;

  private searchSubject = new Subject<string>();
  private liveCheckSubject = new Subject<void>();
  private searchSub: Subscription;
  private liveCheckSub: Subscription;
  private pollInterval: any;

  private readonly transactionTypeLabels: Record<number, string> = {
    1: "Market",
    2: "Limit",
    3: "Limit Fill or Kill",
    4: "Limit FAK",
    5: "Market Fill or Kill",
    6: "Stop Loss",
    7: "Stop Limit",
    8: "On Stop",
    12: "Market on Close",
    13: "Market Anonymous",
    14: "Limit Anonymous",
    15: "Limit Iceberg",
    26: "Pre Market",
    27: "After Market",
    28: "Imm. or Cancel",
  };

  constructor(
    private _localize: LocalizeService,
    private _element: ElementRef,
    private _authService: AuthService,
    private _service: ControlTableService,
  ) {}

  ngOnInit(): void {
    this.searchSub = this.searchSubject
      .pipe(
        debounceTime(300),
        switchMap((q) => {
          if (!q || q.trim().length < 3) return of([]);
          return this._service.searchSecurities(q.trim(), this.currency);
        }),
      )
      .subscribe((results) => {
        this.searchResults = results ?? [];
        this.showSearchResults = true;
      });

    this.liveCheckSub = this.liveCheckSubject
      .pipe(debounceTime(700))
      .subscribe(() => this.runLiveCheck());

    if (this.security) this.initForSecurity();
    setTimeout(
      () => this._localize.applyTranslations(this._element.nativeElement),
      0,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.security && this.security) {
      this.initForSecurity();
    }
  }

  ngOnDestroy(): void {
    if (this.searchSub) this.searchSub.unsubscribe();
    if (this.liveCheckSub) this.liveCheckSub.unsubscribe();
    if (this.pollInterval) clearInterval(this.pollInterval);
  }

  private initForSecurity(): void {
    const sec = this.security;
    if (!sec) return;
    this.isDomestic =
      sec.SecStockExchange === 12 || sec.SecStockExchange === 15;
    this.resetForm();
    this.loadAllowedTransactionTypes(sec.SecStockExchange);
    this.loadCashBalance();
    this.loadHeldQuantity();
    this.loadSecReservationPercent();
    this.startPricePolling();
  }

  onSearch(): void {
    this.searchSubject.next(this.searchQuery);
  }

  hideSearchResults(): void {
    setTimeout(() => (this.showSearchResults = false), 200);
  }

  pickSecurity(sec: TraderSecurityDtoModel): void {
    this.security = {
      SecIndex: sec.SecIndex,
      SecNumber: sec.SecNumber,
      SecISIN: sec.SecISIN,
      SecStockExchange: sec.SecStockExchange,
      SecSymbol: sec.SecSymbol,
      SecName: sec.SecName,
      SecCurrSymbol: sec.SecCurrSymbol,
      FxRate: sec.FxRate,
      StockExchangeCode: sec.StockExchangeCode,
      StockExchangeName: sec.StockExchangeName,
      EODHDStockExchangeCode: sec.EODHDStockExchangeCode,
      SecType: sec.SecType,
      SecCurr: sec.SecCurr,
      SecFactor: sec.SecFactor,
      SecContractSize: sec.SecContractSize,
    };
    this.searchQuery = "";
    this.searchResults = [];
    this.showSearchResults = false;
    this.initForSecurity();
    this.reapplyLocalize();
  }

  private resetForm(): void {
    this.tradeAmount = 0;
    this.tradePrice = 0;
    this.tradeStopPrice = 0;
    this.tradeTotal = 0;
    this.currentPrice = 0;
    this.secReservationPercent = 1;
    this.liveCheckAmounts = null;
    this.orderError = "";
    this.orderResult = null;
    this.selectedValidity = "Day";
    this.goodTillDate = "";
  }

  private loadAllowedTransactionTypes(secStockExchange: number): void {
    this._service
      .allowedTransactionTypes(secStockExchange)
      .subscribe((types) => {
        this.allowedTransactionTypes = [
          ...new Set((types ?? []).map((t) => +t)),
        ];
        const first = this.allowedTransactionTypes[0];
        if (first !== undefined) this.onTransactionTypeChange(first);
      });
  }

  private loadAllowedOrderTypes(transactionType: number): void {
    if (!this.security) return;
    this._service
      .allowedOrderTypes(this.security.SecStockExchange, transactionType)
      .subscribe((types) => {
        this.allowedOrderTypes = (types ?? []).map((t) => +t);
      });
  }

  private loadCashBalance(): void {
    if (!this.security) return;
    this.cashLoading = true;
    this.availableLiquidity = 0;
    this._service
      .cashBalanceForSecurity(this.security, this.currency)
      .subscribe((balance) => {
        this.cashLoading = false;
        this.availableLiquidity = balance ?? 0;
      });
  }

  private loadHeldQuantity(): void {
    if (!this.security) return;
    this.heldLoading = true;
    this.heldQuantity = 0;
    this._service
      .positionQuantityForSecurity(this.security)
      .subscribe((qty) => {
        this.heldLoading = false;
        this.heldQuantity = qty ?? 0;
      });
  }

  private loadSecReservationPercent(): void {
    if (!this.security) return;
    this._service
      .secReservationPercent(this.security.SecIndex)
      .subscribe((percent) => {
        this.secReservationPercent =
          percent != null && percent > 0 ? percent : 1;
      });
  }

  private startPricePolling(): void {
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.loadPrice();
    this.pollInterval = setInterval(() => this.loadPrice(), 5000);
  }

  private loadPrice(): void {
    if (!this.security) return;
    this._service.realTimeL1(this.security).subscribe((data) => {
      if (!data || data.length === 0) return;
      const l1 = data[0];
      const hasMid =
        this.isDomestic &&
        l1.bid != null &&
        l1.bid > 0 &&
        l1.ask != null &&
        l1.ask > 0;
      const livePrice =
        hasMid && l1.bid != null && l1.ask != null
          ? (l1.bid + l1.ask) / 2
          : l1.price;
      if (livePrice && livePrice > 0) {
        this.currentPrice = livePrice;
        if (this.isImmediateOrder) {
          this.tradePrice = Math.round(this.currentPrice * 100) / 100;
        }
      }
    });
  }

  get symbol(): string {
    if (!this.security) return "";
    return (
      this.security.SecSymbol?.trim() || this.security.SecNumber?.trim() || ""
    );
  }

  get securityCurrency(): string {
    return (this.security && this.security.SecCurrSymbol) || this.currency;
  }

  get orderTypeItems(): { id: number; label: string }[] {
    return this.allowedTransactionTypes.map((type) => ({
      id: type,
      label: this.transactionTypeLabels[type] ?? `Type ${type}`,
    }));
  }

  get isImmediateOrder(): boolean {
    return [1, 5, 28].includes(this.selectedTransactionType);
  }

  get showStopPriceField(): boolean {
    return this.selectedTransactionType === 7 && !this.isDomestic;
  }

  get priceLabel(): string {
    if (this.selectedTransactionType === 7)
      return this._localize.getLocalizedResource("Res572") || "Limit Cena";
    return this._localize.getLocalizedResource("Res3534") || "Cena";
  }

  get validityOptions(): { id: string; label: string }[] {
    if (this.isImmediateOrder) return [];
    const day = {
      id: "Day",
      label: this._localize.getLocalizedResource("Res3330") || "Dan",
    };
    const gtd = {
      id: "GTD",
      label: this._localize.getLocalizedResource("Res3331") || "Do datuma",
    };
    const gtc = {
      id: "GTC",
      label: this._localize.getLocalizedResource("Res3332") || "Do opoziva",
    };
    if (this.isDomestic) return [day, gtd];
    return [day, gtd, gtc];
  }

  get showDatePicker(): boolean {
    return !this.isImmediateOrder && this.selectedValidity === "GTD";
  }

  get todayDateString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(d.getDate()).padStart(2, "0")}`;
  }

  private get fxRate(): number {
    return this.security && this.security.FxRate > 0 ? this.security.FxRate : 1;
  }

  private get secFactor(): number {
    return this.security && this.security.SecFactor > 0
      ? this.security.SecFactor
      : 1;
  }

  private get secContractSize(): number {
    return this.security && this.security.SecContractSize > 0
      ? this.security.SecContractSize
      : 1;
  }

  get isBuy(): boolean {
    return this.side === "Buy";
  }

  get buyAllowedForType(): boolean {
    return (
      this.allowedOrderTypes.length === 0 || this.allowedOrderTypes.includes(0)
    );
  }

  get sellAllowedForType(): boolean {
    return (
      this.allowedOrderTypes.length === 0 || this.allowedOrderTypes.includes(1)
    );
  }

  get exceedsLiquidity(): boolean {
    return (
      this.isBuy &&
      this.availableLiquidity > 0 &&
      this.tradeTotal * this.fxRate > this.availableLiquidity
    );
  }

  get noFunds(): boolean {
    return this.isBuy && this.availableLiquidity <= 0 && this.tradeAmount > 0;
  }

  get noHoldings(): boolean {
    return !this.isBuy && this.heldQuantity <= 0;
  }

  get exceedsSellAmount(): boolean {
    return (
      !this.isBuy &&
      this.heldQuantity > 0 &&
      this.tradeAmount > this.heldQuantity
    );
  }

  get canSubmit(): boolean {
    if (!this.security || this.tradeAmount <= 0) return false;
    if (this.isSubmitting || this.isChecking) return false;
    if (!this.isImmediateOrder && this.tradePrice <= 0) return false;
    if (this.isBuy) {
      return !this.exceedsLiquidity && !this.noFunds && this.buyAllowedForType;
    }
    return (
      !this.noHoldings && !this.exceedsSellAmount && this.sellAllowedForType
    );
  }

  get liveCheckTotal(): number | null {
    if (!this.liveCheckAmounts) return null;
    const key = Object.keys(this.liveCheckAmounts).find(
      (k) => k.toLowerCase() === "ukupno",
    );
    return key ? this.liveCheckAmounts[key] : null;
  }

  setSide(side: "Buy" | "Sell"): void {
    this.side = side;
    this.recomputeTotal();
    this.scheduleLiveCheck();
  }

  onTransactionTypeChange(type: number): void {
    this.selectedTransactionType = +type;
    this.selectedValidity = this.isImmediateOrder ? "Immediate" : "Day";
    if (!this.showDatePicker) this.goodTillDate = "";
    this.loadAllowedOrderTypes(this.selectedTransactionType);
    if (this.isImmediateOrder && this.currentPrice > 0) {
      this.tradePrice = Math.round(this.currentPrice * 100) / 100;
    }
    this.recomputeTotal();
    this.scheduleLiveCheck();
  }

  onValidityChange(v: string): void {
    this.selectedValidity = v;
    if (!this.showDatePicker) this.goodTillDate = "";
  }

  onAmountChange(v: number): void {
    this.tradeAmount = +v || 0;
    this.recomputeTotal();
    this.scheduleLiveCheck();
  }

  onPriceChange(v: number): void {
    this.tradePrice = +v || 0;
    this.recomputeTotal();
    this.scheduleLiveCheck();
  }

  onStopPriceChange(v: number): void {
    this.tradeStopPrice = +v || 0;
  }

  private recomputeTotal(): void {
    const price = this.isImmediateOrder ? this.currentPrice : this.tradePrice;
    this.tradeTotal =
      (price * this.tradeAmount * this.secContractSize) / this.secFactor;
  }

  private scheduleLiveCheck(): void {
    this.liveCheckAmounts = null;
    if (this.tradeAmount > 0 && this.security) {
      this.liveCheckLoading = true;
      this.liveCheckSubject.next();
    }
  }

  private runLiveCheck(): void {
    if (!this.security || this.tradeAmount <= 0) {
      this.liveCheckLoading = false;
      return;
    }
    this._service.orderCheck(this.buildOrder()).subscribe({
      next: (res) => {
        this.liveCheckLoading = false;
        this.liveCheckAmounts =
          res && res.amounts && Object.keys(res.amounts).length > 0
            ? res.amounts
            : null;
        this.reapplyLocalize();
      },
      error: () => {
        this.liveCheckLoading = false;
        this.liveCheckAmounts = null;
      },
    });
  }

  private getOrderPeriod(): number {
    if (this.isImmediateOrder) return 1;
    switch (this.selectedValidity) {
      case "Day":
        return 3;
      case "GTC":
        return 6;
      case "GTD":
        return 4;
      default:
        return 3;
    }
  }

  private toLocalISO(d: Date): string {
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
      d.getHours(),
    )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  private buildOrder(): any {
    const expiryDate =
      this.showDatePicker && this.goodTillDate
        ? `${this.goodTillDate}T00:00:00`
        : this.toLocalISO(new Date());
    return {
      ...this.security,
      OrderType: this.isBuy ? 0 : 1,
      TradingType: 1,
      OrderPeriod: this.getOrderPeriod(),
      TransactionType: this.selectedTransactionType,
      SecurityIndex: this.security?.SecIndex ?? 0,
      PartnerIndex: this._authService.UserInfo?.PartnerIndex ?? 0,
      Quantity: this.tradeAmount,
      Price: this.isImmediateOrder
        ? this.isBuy && this.selectedTransactionType === 1
          ? Math.round(this.currentPrice * this.secReservationPercent * 100) /
            100
          : this.currentPrice
        : this.tradePrice,
      StopPrice:
        this.selectedTransactionType === 7 ? this.tradeStopPrice : null,
      ExpiryDate: expiryDate,
    };
  }

  submit(): void {
    if (!this.canSubmit) return;
    this.orderError = "";
    this.orderResult = null;
    this.isChecking = true;

    this._authService.getUserInfo().subscribe({
      next: () => this.runCheckThenInsert(),
      error: () => this.runCheckThenInsert(),
    });
  }

  private runCheckThenInsert(): void {
    this._service.orderCheck(this.buildOrder()).subscribe({
      next: (res) => {
        this.isChecking = false;
        if (res && res.success) {
          this.doInsert();
        } else {
          this.orderError =
            (res && res.description) ||
            this._localize.getLocalizedResource("Res1144") ||
            "Provjera naloga nije uspjela.";
          this.reapplyLocalize();
        }
      },
      error: (err) => {
        this.isChecking = false;
        this.orderError =
          err?.message ||
          this._localize.getLocalizedResource("Res1145") ||
          "Greška pri provjeri naloga.";
        this.reapplyLocalize();
      },
    });
  }

  private doInsert(): void {
    this.isSubmitting = true;
    this._service.orderInsert(this.buildOrder()).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.orderResult = {
          success: res.success,
          description: res.description,
        };
        if (res.success) {
          this.tradeAmount = 0;
          this.tradeTotal = 0;
          this.liveCheckAmounts = null;
          this.loadCashBalance();
          this.loadHeldQuantity();
        }
        this.reapplyLocalize();
      },
      error: (err) => {
        this.isSubmitting = false;
        this.orderResult = {
          success: false,
          description:
            err?.message ||
            this._localize.getLocalizedResource("Res1146") ||
            "Greška pri unosu naloga.",
        };
        this.reapplyLocalize();
      },
    });
  }

  removeFromTable(): void {
    if (this.security) this.removeSecurity.emit(this.security.SecIndex);
  }

  close(): void {
    if (this.isSubmitting) return;
    this.closed.emit();
  }

  formatN(value: number | null | undefined, decimals = 2): string {
    if (value === null || value === undefined || isNaN(value as number))
      return "";
    return (kendo as any).toString(value, "n" + decimals);
  }

  private reapplyLocalize(): void {
    setTimeout(
      () => this._localize.applyTranslations(this._element.nativeElement),
      0,
    );
  }
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table-order-modal.component.ts